import type { ActiveGamesAndPacksResponse, Store as StoreType } from '../../../../services/authService';
import type {
  CreateAuditPackValidationIssue,
  CreateAuditRetailerContext,
  CreateAuditValidationIssue,
  CreateAuditValidationState,
  CreateManualAuditPayload,
  InitialValues,
  TicketDetail,
} from '../../../../types/auditFormTypes';
import type { Game } from '../../../../types/features/games';
import type { ProcessedAuditTicket } from '../../../../utils/auditDataProcessor';
import { validateTicketDetailsAgainstActiveGamesAndPacks } from '../../../../utils/fixOcrDataProcessors';
import { buildTicketNumberValue } from '../../../../utils/ticketFormatters';

const emptyCreateAuditTicket: TicketDetail = {
  game: '',
  name: '',
  pack: '',
  range: '',
  isMissing: false,
  userAccepted: false,
  haveError: false,
  errorMessage: '',
  rangeMismatch: false,
  rangeAccepted: false,
  rangeTooltip: '',
  newValues: {},
  newField: true,
  ticketsPerPack: 0,
  ticketPrice: 0,
};

export const createEmptyCreateAuditTicket = (): TicketDetail => ({
  ...emptyCreateAuditTicket,
});

export const buildCreateAuditGamesMap = (gamesResponse: Game[]): Map<string, number> => {
  const gamesMap = new Map<string, number>();

  gamesResponse.forEach((game) => {
    if (!game?.game_id) {
      return;
    }

    gamesMap.set(String(game.game_id), Number(game.cost_per_ticket) || 0);
    gamesMap.set(`${game.game_id}-ticketsPerPack`, Number(game.tickets_per_pack) || 0);
  });

  return gamesMap;
};

export const buildCreateAuditLookupDateTime = (shiftDate: string, shiftEndTime: string): string => {
  if (!shiftDate || !shiftEndTime) {
    return '';
  }

  try {
    const timeParts = shiftEndTime.split(':');
    const normalizedTime = timeParts.length >= 3
      ? shiftEndTime
      : `${timeParts[0] || '00'}:${timeParts[1] || '00'}:00`;

    return new Date(`${shiftDate}T${normalizedTime}Z`).toISOString();
  } catch {
    return '';
  }
};

export const buildCreateAuditRetailerContext = (
  storeDetails: StoreType | null | undefined,
): CreateAuditRetailerContext => {
  const retailerId = storeDetails?.lottery_reseller_id || storeDetails?.retailer_id || '';

  return {
    retailerId,
    retailerFromStore: Boolean(retailerId),
  };
};

export const normalizeCreateAuditPreviousTickets = (
  ticketDetails: Array<ProcessedAuditTicket & Pick<TicketDetail, 'ticketPrice' | 'ticketsPerPack'>>,
): TicketDetail[] => {
  if (!Array.isArray(ticketDetails) || ticketDetails.length === 0) {
    return [];
  }

  return ticketDetails.map((ticket) => ({
    ...createEmptyCreateAuditTicket(),
    game: String(ticket?.game || ''),
    name: ticket?.name || '',
    pack: String(ticket?.pack || ''),
    range: ticket?.range || '',
    newField: false,
    ticketPrice: Number(ticket?.ticketPrice) || 0,
    ticketsPerPack: Number(ticket?.ticketsPerPack) || 0,
  }));
};

export const buildCreateAuditInitialValues = ({
  retailerId,
  retailerFromStore,
  shiftDate,
  shiftStartTime,
  shiftEndTime,
  ticketDetails,
}: {
  retailerId: string;
  retailerFromStore: boolean;
  shiftDate: string;
  shiftStartTime: string;
  shiftEndTime: string;
  ticketDetails: TicketDetail[];
}): InitialValues => ({
  action: 'finalize',
  confidence_score: 100,
  notes: '',
  shift_date: shiftDate,
  shift_start_time: shiftStartTime,
  shift_end_time: shiftEndTime,
  retailer_id: retailerId,
  retailer_from_store: retailerFromStore,
  ticket_details: ticketDetails.length > 0 ? ticketDetails : [createEmptyCreateAuditTicket()],
});

export const buildCreateAuditFormStateKey = (
  formData: InitialValues,
  invalidGames: CreateAuditValidationIssue[],
  invalidPacks: CreateAuditPackValidationIssue[],
) => JSON.stringify({ formData, invalidGames, invalidPacks });

export const validateCreateAuditFormState = (
  formData: InitialValues,
  activeGamesAndPacks: ActiveGamesAndPacksResponse | null | undefined,
) : CreateAuditValidationState => {
  const validationResult = Array.isArray(formData?.ticket_details)
    ? validateTicketDetailsAgainstActiveGamesAndPacks(formData.ticket_details, activeGamesAndPacks)
    : {
        ticketDetails: formData?.ticket_details || [],
        invalidGames: [] as CreateAuditValidationIssue[],
        invalidPacks: [] as CreateAuditPackValidationIssue[],
      };

  const validatedFormData = {
    ...formData,
    ticket_details: validationResult.ticketDetails,
  };

  return {
    formData: validatedFormData,
    invalidGames: validationResult.invalidGames,
    invalidPacks: validationResult.invalidPacks,
    stateKey: buildCreateAuditFormStateKey(
      validatedFormData,
      validationResult.invalidGames,
      validationResult.invalidPacks,
    ),
  };
};

const normalizeRangeEnd = (ticket: TicketDetail): string => {
  const [, rawRangeEnd = ''] = String(ticket.range || '').split('-');

  if (rawRangeEnd.toUpperCase() === 'END') {
    return String(ticket.ticketsPerPack || '').replace(/\D/g, '').padStart(3, '0').slice(-3);
  }

  return rawRangeEnd.replace(/\D/g, '').padStart(3, '0').slice(-3);
};

const DEFAULT_AUDIT_BARCODE_FORMAT = '0,4|4,7|12,3|26';

export const buildCreateManualAuditPayload = (
  formData: InitialValues,
  options?: {
    barcodeFormat?: string;
  },
): CreateManualAuditPayload => ({
  date_time: `${formData.shift_date}T${formData.shift_end_time}Z`,
  starting_ticket_numbers: (formData.ticket_details || []).map((ticket: TicketDetail) => {
    const rangeEnd = normalizeRangeEnd(ticket);
    const format = options?.barcodeFormat || DEFAULT_AUDIT_BARCODE_FORMAT;

    return buildTicketNumberValue({
      format,
      gameNumber: String(ticket.game || ''),
      packId: String(ticket.pack || ''),
      ticketNumber: Number.parseInt(rangeEnd, 10) || 0,
    });
  }),
});
